import React, { useState } from "react";

/**
 * Generate a color based on a string (name)
 * This ensures the same name always gets the same color
 */
const generateColorFromString = (str) => {
  if (!str) return "#6B7280"; // Default gray color
  
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate a color with good contrast
  const hue = Math.abs(hash % 360);
  const saturation = 60 + (Math.abs(hash) % 20); // 60-80% saturation
  const lightness = 45 + (Math.abs(hash) % 15); // 45-60% lightness
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

/**
 * Get initials from name
 */
const getInitials = (firstName, lastName, email) => {
  if (firstName && lastName) {
    return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
  }
  if (firstName) {
    return firstName.substring(0, 2).toUpperCase();
  }
  if (lastName) {
    return lastName.substring(0, 2).toUpperCase();
  }
  if (email) {
    return email.substring(0, 2).toUpperCase();
  }
  return "U";
};

/**
 * Avatar Component
 * Displays user avatar image or initials if image is not available
 * 
 * @param {string} src - Image URL
 * @param {string} firstName - User's first name
 * @param {string} lastName - User's last name
 * @param {string} email - User's email (fallback for initials)
 * @param {string} alt - Alt text for image
 * @param {string} className - Additional CSS classes
 * @param {string|number} size - Size of avatar (e.g., "40px", "10", "w-10 h-10")
 * @param {boolean} showBorder - Whether to show border
 */
const Avatar = ({
  src,
  firstName,
  lastName,
  email,
  alt,
  className = "",
  size = "40px",
  showBorder = false,
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const initials = getInitials(firstName, lastName, email);
  const displayName = `${firstName || ""} ${lastName || ""}`.trim() || email || "User";
  const backgroundColor = generateColorFromString(displayName);
  
  // Determine size classes
  const getSizeClasses = () => {
    if (typeof size === "string" && size.includes("w-")) {
      return size;
    }
    if (typeof size === "number") {
      return `w-${size} h-${size}`;
    }
    return "";
  };

  const sizeClasses = getSizeClasses();
  const sizeStyle = sizeClasses ? {} : { width: size, height: size, minWidth: size, minHeight: size };

  // Calculate font size for initials
  const getFontSize = () => {
    if (typeof size === "number") {
      return `${size * 0.4}px`;
    }
    if (typeof size === "string") {
      if (size.includes("w-")) {
        // Extract number from Tailwind class like "w-10" -> 10
        const match = size.match(/w-(\d+)/);
        if (match) {
          const num = parseInt(match[1]);
          // Tailwind spacing: 10 = 2.5rem = 40px, so calculate accordingly
          const remValue = num * 0.25; // Convert Tailwind number to rem
          return `${remValue * 0.4}rem`; // 40% of the size
        }
        return "0.875rem"; // Default fallback
      }
      // Handle pixel values like "40px"
      if (size.includes("px")) {
        const num = parseInt(size);
        return `${num * 0.4}px`;
      }
      return `calc(${size} * 0.4)`;
    }
    return "0.875rem";
  };

  // Check if src is a valid image URL (not null, not empty, and not a default placeholder)
  const isValidImageUrl = src && 
                         src !== "" && 
                         !src.includes("pravatar.cc") && 
                         !src.includes("default") &&
                         (src.startsWith("http") || src.startsWith("data:image") || src.startsWith("/"));
  
  const hasImage = isValidImageUrl && !imageError;

  return (
    <div
      className={`rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${
        showBorder ? "border border-slate-200" : ""
      } ${sizeClasses} ${className}`}
      style={{
        ...sizeStyle,
        backgroundColor: hasImage ? "transparent" : backgroundColor,
      }}
    >
      {hasImage ? (
        <img
          src={src}
          alt={alt || displayName}
          className="h-full w-full object-cover"
          onError={() => setImageError(true)}
          onLoad={() => setImageLoaded(true)}
          style={{ display: imageLoaded ? "block" : "none" }}
        />
      ) : (
        <span
          className="text-white font-semibold select-none"
          style={{
            fontSize: getFontSize(),
          }}
        >
          {initials}
        </span>
      )}
    </div>
  );
};

export default Avatar;
